import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  LeftSide,
  LogoLink,
  Content,
  Container,
  RightSide,
  LogoImgWrapper,
  HeaderBurgerWrapper,
  HeaderBurger,
  HeaderBurgerInner,
} from './header.styles';
import { ReactComponent as MeestLogo } from '../../assets/meest-logo.svg';
import { ReactComponent as PolonezLogo } from '../../assets/polonez-logo.svg';
// import LangSwitcher from './lang-switcher/lang-switcher.component';
import HeaderSignin from './header-signin-btn/header-signin.component';
import { useStore } from '../../stores/store';
import HeaderUser from './header-user/header-user.component';
import { AppRoute } from '../../constants/route';
import { VisuallyHidden } from '../../common/visually-hidden/visually-hidden.styles';
import IconButton from '../../common/icon-button/icon-button';
import { useIsMobile } from '../../hooks/use-is-mobile.hook';
import {
  generateLocaleRoute,
  generateMyRoute,
} from '../../utils/uri/uri.utils';
import { client } from '../../config';
import HeaderContacts from './header-contacts/header-contacts.component';
import HeaderRegionSettings from './header-settings-popup/header-settings-popup-holder';
import CrossSaleMenu from './cross-sale-menu/cross-sale-menu.component';
import { isFeatureRequired } from '../../utils/client/client.utils';

const ROUTES_WITHOUT_BURGER_MENU = [
  AppRoute.NOT_FOUND,
  AppRoute.SERVER_ERROR,
  AppRoute.COMING_SOON,
];

export const getLogo = () => {
  const logos = {
    meest: <MeestLogo />,
    polonez: <PolonezLogo />,
  };

  return logos[client] || null;
};
export const getLogoDescription = () => {
  const descriptions = {
    meest: 'Meest logo',
    polonez: 'Polonez logo',
  };

  return descriptions[client] || '';
};

export const getLinkUrl = (
  locale: string,
  hasMarketingPage: boolean,
  isAuthenticated: boolean
) => {
  if (client === 'polonez' && !isAuthenticated && !hasMarketingPage) {
    return `https://polonez.express${locale}/`;
  }

  if (isAuthenticated && !hasMarketingPage) {
    return generateMyRoute(AppRoute.PARCEL_LIST);
  }

  return generateLocaleRoute();
};

const Header = () => {
  const location = useLocation();
  const {
    userStore: { user, loginGup },
    commonStore: { isGuestParcelCreation },
    navStore: {
      isServicesSidebarVisible,
      setServicesSidebarVisibility,
      hasMarketingPage,
    },
    parcelCreationStore: { isParcelCreationInProgress },
    localizationsStore: { localeParam },
  } = useStore();
  const auth = useAuth();

  const mainPagePathname = generateLocaleRoute();
  const isAuthPage = location.pathname.includes(AppRoute.AUTH);
  const isRootPage = location.pathname === AppRoute.ROOT;
  const isMainPage = location.pathname === mainPagePathname;

  const isSimpleHeader =
    isAuthPage || location.pathname.includes(AppRoute.PRIVACY_OPT_OUT);

  const isMobile = useIsMobile();

  const isPageWithoutMenu =
    isMainPage ||
    ROUTES_WITHOUT_BURGER_MENU.some((item) => location.pathname.includes(item));

  const hideHeader = isMobile && isParcelCreationInProgress;

  const logoLinkUrl = getLinkUrl(localeParam, hasMarketingPage, !!user);

  const loginGuestButton = (
    <IconButton
      size='large'
      appearance='text'
      data-testid='header-btn-login'
      $pl='0.8rem'
      $pr='0.8rem'
      $pt='0.8rem'
      $pb='0.8rem'
      $ml='-0.8rem'
      icon={{ glyph: 'login', color: 'primary' }}
      onClick={() =>
        loginGup({
          auth,
          redirectUri: location.pathname,
          isGuestFlow: true,
        })
      }
    />
  );

  let content;
  if (isRootPage) {
    content = <LogoImgWrapper>{getLogo()}</LogoImgWrapper>;
  } else if (isSimpleHeader) {
    content = (
      <Content $justifyContent='center'>
        <LogoLink to={logoLinkUrl}>
          <VisuallyHidden>{getLogoDescription()}</VisuallyHidden>
          {getLogo()}
        </LogoLink>
      </Content>
    );
  } else {
    content = (
      <Content>
        <LeftSide>
          {!isPageWithoutMenu && !isMobile && (
            <HeaderBurgerWrapper
              $isActive={isServicesSidebarVisible}
              onClick={() =>
                setServicesSidebarVisibility(!isServicesSidebarVisible)
              }>
              <HeaderBurger>
                <HeaderBurgerInner />
              </HeaderBurger>
            </HeaderBurgerWrapper>
          )}
          <LogoLink
            to={logoLinkUrl}
            $hasRightVertBorder={isPageWithoutMenu}>
            <VisuallyHidden>{getLogoDescription()}</VisuallyHidden>
            {getLogo()}
          </LogoLink>
          {isPageWithoutMenu && isFeatureRequired('otherProductsRequired') && (
            <CrossSaleMenu />
          )}
        </LeftSide>
        <RightSide>
          {!isMobile && <HeaderContacts />}
          {!isParcelCreationInProgress && <HeaderRegionSettings />}
          {!isAuthPage && user && !isGuestParcelCreation && (
            <HeaderUser isMainPage={isMainPage} />
          )}
          {!isAuthPage && !user && !isGuestParcelCreation && (
            <HeaderSignin isMainPage={isMainPage} />
          )}
          {!isAuthPage && isGuestParcelCreation && loginGuestButton}
        </RightSide>
      </Content>
    );
  }

  if (!hideHeader) {
    return <Container $isAuthPage={isAuthPage}>{content}</Container>;
  }
  return null;
};

export default observer(Header);
